import { Icon, TextField } from "@material-ui/core";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { IWorkspaceResourceSlide, IWorkspaceResourceSlideTemplate, WorkspaceResourceType } from "common/interfaces";
import { PresentationLibraryFilterType, PresentationLibraryPresentation } from "js/controllers/PresentationLibraryPaneController";
import WorkspaceController, { WorkspaceControllerState, getWorkspaceResource, getWorkspaceResourcesTags } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import getUserProfile from "js/core/services/userProfiles";
import { caseInsensitiveSort, trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { PresentationLibraryList } from "js/editor/PresentationLibrary/PresentationLibraryList";
import { PresentationLibraryPaneControllerInitializer } from "js/editor/PresentationLibrary/PresentationLibraryPaneControllerInitializer";
import { t } from "js/i18n";
import Avatar from "js/react/components/Avatar";
import AddTagsDialog from "js/react/components/Dialogs/AddTagsDialog";
import { BeautifulDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ConfirmationCheckboxDialog from "js/react/components/Dialogs/ConfirmationCheckboxDialog";
import { Gap10, Gap20, Gap30 } from "js/react/components/Gap";
import { FlexBox, ScrollBox } from "js/react/components/LayoutGrid";
import Loadable from "js/react/components/Loadable";
import { RedSwitch } from "js/react/components/Switch";
import TagsList from "js/react/components/TagsList";
import {
    BlueButton,
    Section,
    UIPane,
    UIPaneContents,
    UIPaneHeader,
} from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { SlideThumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid.js";

const logger = getLogger(LogGroup.TEAMS);

const SlideThumbnailContainer = styled.div`
    width: 640px;
    height: 360px;
    position: relative;
    
    .edit-message {
        opacity: 0;
        position: absolute;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 100%;
        display: flex;
        align-items:  center;
        justify-content: center;
        padding: 50px;
        div {
          color: white;
          font-size: 18px;
          font-weight: 600;
          padding: 15px 20px;
          background: ${themeColors.ui_blue};
          text-align: center;
        }
    }
    &:hover {
      .edit-message {
        opacity: 1;
      }
    }
`;

const StatsContainer = styled.div`
    background: #f1f1f1;   
    width: 100%;
    padding: 15px;
    font-weight: 600;
    .MuiIcon-root {
        margin-right: 10px;
    }
    & > div + div {
        margin-top: 10px;
    }
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    label {
        font-size: 13px;
        width: 80px;
    }
    .avatar {
        margin-right: 10px;
    }
    .name {
        font-size: 14px;
        font-weight: 600;
        color: #333;
    }
    .date { 
        margin-left: 20px;
        font-size: 14px;
        color: #666;
    }
`;

interface TeamSlideDialogProps {
    slideId: string;
    closeDialog?: () => void;
}

const TeamSlideDialog = WorkspaceController.withInitializedState(
    function TeamSlideDialog(props: TeamSlideDialogProps & WorkspaceControllerState) {
        const { slideId, closeDialog } = props;

        const originalSlideResourceRef = useRef<IWorkspaceResourceSlide | IWorkspaceResourceSlideTemplate | null>(null);

        const [slideResource, setSlideResource] = useState<IWorkspaceResourceSlide | IWorkspaceResourceSlideTemplate | null>(null);
        const [presentationIds, setPresentationIds] = useState<string[] | null>(null);
        const [ready, setReady] = useState<boolean>(false);

        useEffect(() => {
            (async () => {
                const slideResource = await getWorkspaceResource({ workspaceId: WorkspaceController.workspaceId, resourceId: slideId }) as IWorkspaceResourceSlide | IWorkspaceResourceSlideTemplate;
                if (!slideResource) {
                    logger.error(new Error("Slide resource not found"), "[TeamSlideDialog] slide resource not found", { slideId });
                    ShowErrorDialog({
                        error: t("Slide resource not found"),
                        message: t("The slide resource was not found. Please try again.")
                    });
                    closeDialog?.();
                    return;
                }

                originalSlideResourceRef.current = _.cloneDeep(slideResource);
                setSlideResource(slideResource);

                if (slideResource.metadata.createdBy) {
                    const createdUser = await getUserProfile(slideResource.metadata.createdBy);
                    setCreatedUser(createdUser);
                } else {
                    setCreatedUser(null);
                }

                if (slideResource.metadata.modifiedBy) {
                    const modifiedUser = await getUserProfile(slideResource.metadata.modifiedBy);
                    setModifiedUser(modifiedUser);
                } else {
                    setModifiedUser(null);
                }
            })();
        }, [slideId]);

        useEffect(() => {
            if (!slideResource) {
                setPresentationIds(null);
                return;
            }

            const presentationIds = slideResource.metadata.usedInPresentations;
            setPresentationIds(presentationIds);
            setReady(true);
        }, [slideResource]);

        const [createdUser, setCreatedUser] = useState<any>(null);
        const [modifiedUser, setModifiedUser] = useState<any>(null);

        const handlePresentationDoubleClick = async (presentation: PresentationLibraryPresentation) => {
            const slideIndex = presentation.slideIds.indexOf(slideResource.resourceId) + 1;

            AppController.showEditor({
                presentationId: presentation.id,
                slideIndex,
                openInNewTab: true
            });

            trackActivity("SharedSlideProperties", "OpenPresentationFromUsedIn", null, null, {
                presentation_id: presentation.id,
                shared_slide_id: slideId
            });
        };

        const renderName = user => {
            if (user?.displayName) {
                return <span className="fullName">{user?.displayName}</span>;
            } else {
                return <span className="fullName">{user?.email}</span>;
            }
        };

        const handleEditSlide = () => {
            closeDialog();

            AppController.showSlideEditor({
                slideId: slideResource.resourceId,
                resourceType: slideResource.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE ? "slide-templates" : "shared-slides"
            });
        };

        const handleDisableSlideToggle = async (isDisabled: boolean) => {
            if (!isDisabled) {
                ShowDialog(ConfirmationCheckboxDialog, {
                    title: t("Are you sure you want to hide this Shared Slide?"),
                    message: t("Hiding a Shared Slide prevents it from being used in future presentations."),
                    okButtonLabel: t("OK"),
                    acceptCallback: async () => {
                        setSlideResource(prev => {
                            if (!prev) return prev;
                            return {
                                ...prev,
                                metadata: {
                                    ...prev.metadata,
                                    isDisabled: !isDisabled
                                }
                            };
                        });
                        trackActivity("SharedSlideLibrary", "SlideDisabled", null, null, { slide_id: slideResource.resourceId }, { audit: true });
                    }
                });
                return;
            }
            setSlideResource(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    metadata: {
                        ...prev.metadata,
                        isDisabled: !isDisabled
                    }
                };
            });
        };

        const handleTextChange = (text, type) => {
            setSlideResource(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    metadata: {
                        ...prev.metadata,
                        [type]: text
                    }
                };
            });
        };

        const handleRemoveTag = inputTag => {
            setSlideResource(prev => {
                if (!prev) return prev;
                // Remove the tag from the original array, using case-insensitive comparison
                // since the inputTag comes from the sorted/trimmed tagsList
                const newTags = prev.tags.filter(tag => _.trim(tag).toLowerCase() !== _.trim(inputTag).toLowerCase());
                return {
                    ...prev,
                    tags: newTags
                };
            });
        };

        const handleAddTag = async () => {
            const existingTags = await getWorkspaceResourcesTags({ workspaceId: WorkspaceController.workspaceId, resourceTypes: [WorkspaceResourceType.SLIDE, WorkspaceResourceType.SLIDE_TEMPLATE] });
            ShowDialog(AddTagsDialog, {
                activeTags: (slideResource.tags || []).map(_.trim),
                existingTags: existingTags.map(tag => tag.tag),
                acceptCallback: (newTags: string[]) => {
                    setSlideResource(prev => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            tags: newTags
                        };
                    });
                },
            });
        };

        const handleCloseAndSave = async () => {
            if (!slideResource || !originalSlideResourceRef.current) {
                closeDialog && closeDialog();
                return;
            }

            const getNormalizedResource = resource => {
                return {
                    ...resource,
                    tags: caseInsensitiveSort(resource?.tags || [], _.trim),
                    metadata: {
                        ...resource.metadata,
                        name: resource.metadata.name || "",
                        description: resource.metadata.description || ""
                    }
                };
            };

            const normalizedCurrentResource = getNormalizedResource(slideResource);
            const normalizedOriginalResource = getNormalizedResource(originalSlideResourceRef.current);

            if (!_.isEqual(normalizedCurrentResource, normalizedOriginalResource)) {
                try {
                    await withProgressDialog({
                        title: t("Saving your changes..."),
                        action: () => WorkspaceController.updateWorkspaceResource(slideResource)
                    });
                    // Only close dialog after successful save
                    closeDialog && closeDialog();
                } catch (err) {
                    logger.error(err, "[TeamSlideDialog] workspace resource update failed");
                    ShowErrorDialog({
                        error: t("Failed to update team slide."),
                        message: err.message || t("An unknown error occurred.")
                    });
                    // Don't close dialog on error so user can see the error message
                }
            } else {
                // No changes detected, close dialog immediately
                closeDialog && closeDialog();
            }
        };

        const tagsList = caseInsensitiveSort(slideResource?.tags || [], _.trim);

        return (
            <BeautifulDialog maxWidth="lg" closeDialog={handleCloseAndSave} closeButton>
                <UIPane style={{ overflow: "hidden" }}>
                    <UIPaneHeader>
                        {slideResource?.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE
                            ? t("Slide Template Properties")
                            : t("Shared Slide Properties")}
                    </UIPaneHeader>
                    <ScrollBox id="shared-slide-properties-content">
                        <UIPaneContents style={{ minHeight: 200 }}>
                            <Loadable isLoading={!ready}>
                                <Section>
                                    <Gap10 />
                                    <FlexBox top left>
                                        <SlideThumbnailContainer>
                                            <SlideThumbnail slideId={slideId} highQuality />
                                            <div className="edit-message">
                                                <BlueButton onClick={handleEditSlide}>{t("Edit Slide...")}</BlueButton>
                                            </div>
                                        </SlideThumbnailContainer>
                                        <Gap30 />
                                        <FlexBox column top left width={500}>
                                            <TextField
                                                id="library-name"
                                                autoFocus
                                                label={t("Name")}
                                                fullWidth
                                                value={slideResource?.metadata.name || ""}
                                                onChange={event => handleTextChange(event.target.value, "name")}
                                            />
                                            <Gap30 />

                                            <UserInfo>
                                                <label>{t("Created by")}</label>
                                                <Avatar url={createdUser?.photoURL} />
                                                <div className="name">{renderName(createdUser)}</div>
                                                <div className="date">{moment.utc(slideResource?.createdAt).local().format("LL")}</div>
                                            </UserInfo>
                                            <Gap20 />
                                            {modifiedUser &&
                                                <UserInfo>
                                                    <label>{t("Modified by")}</label>
                                                    <Avatar url={modifiedUser?.photoURL} />
                                                    <div className="name">{renderName(modifiedUser)}</div>
                                                    <div className="date">{moment.utc(slideResource?.metadata.contentModifiedAt).local().format("LL")}</div>
                                                </UserInfo>
                                            }

                                            <Gap20 />

                                            <StatsContainer>
                                                <FlexBox left middle>
                                                    <Icon>star</Icon>
                                                    {slideResource?.metadata.ratedByUsers.length} {slideResource?.metadata.ratedByUsers.length === 1 ? t("like") : t("likes")}
                                                </FlexBox>

                                                {slideResource?.resourceType !== WorkspaceResourceType.SLIDE_TEMPLATE &&
                                                    <FlexBox left middle>
                                                        <Icon>person</Icon>
                                                        {t("Used by {{count}} {{people}}", {
                                                            count: slideResource?.metadata.usedByUsers.length,
                                                            people: slideResource?.metadata.usedByUsers.length == 1 ? t("person") : t("people")
                                                        })}
                                                    </FlexBox>
                                                }

                                                {slideResource?.resourceType !== WorkspaceResourceType.SLIDE_TEMPLATE &&
                                                    <FlexBox left middle>
                                                        <Icon>cloud_download</Icon>
                                                        {t("Used in {{count}} {{presentations}}", {
                                                            count: slideResource?.metadata.usedInPresentations.length,
                                                            presentations: slideResource?.metadata.usedInPresentations.length === 1 ? t("presentation") : t("presentations")
                                                        })}
                                                    </FlexBox>
                                                }
                                            </StatsContainer>
                                            <Gap20 />
                                            <RedSwitch
                                                id="hideSlideSwitch"
                                                checked={slideResource?.metadata.isDisabled}
                                                onChange={() => handleDisableSlideToggle(slideResource?.metadata.isDisabled)}
                                                value="disableSlide"
                                                label={t("Hide This Slide")}
                                            />
                                        </FlexBox>
                                    </FlexBox>
                                    <Gap20 />
                                    <TextField multiline
                                        fullWidth
                                        value={slideResource?.metadata.description || ""}
                                        placeholder={t("Add a description or instructions for this slide")}
                                        variant="outlined"
                                        onChange={event => handleTextChange(event.target.value, "description")}
                                    />

                                </Section>
                                <Section>
                                    <FlexBox left middle fillWidth>
                                        <div className="section-title">{t("Tags")}</div>
                                        <Gap20 />
                                        <TagsList
                                            tags={tagsList}
                                            onRemove={handleRemoveTag}
                                            onClick={handleAddTag}
                                        />
                                    </FlexBox>
                                </Section>
                                {slideResource?.resourceType === WorkspaceResourceType.SLIDE && presentationIds &&
                                    <Section title={t("Where is this slide used")}>
                                        {presentationIds.length === 0 && <label>{t("This team slide is not used in any presentations.")}</label>}
                                        {presentationIds.length > 0 &&
                                            <PresentationLibraryPaneControllerInitializer
                                                pane={PresentationLibraryFilterType.PRESENTATION_IDS}
                                                presentationIds={presentationIds}
                                            >
                                                <PresentationLibraryList
                                                    onDoubleClick={handlePresentationDoubleClick}
                                                />
                                            </PresentationLibraryPaneControllerInitializer>
                                        }
                                    </Section>
                                }
                            </Loadable>
                        </UIPaneContents>
                    </ScrollBox>
                </UIPane>
            </BeautifulDialog >
        );
    }
);

export default TeamSlideDialog;
